import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { InventoryReportFilterModel } from 'src/app/models/inventory/inventory-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryPeriodService } from 'src/app/services/inventory/inventory-period.service';
import { InventoryReportService } from 'src/app/services/inventory/inventory-report.service';
import { InventoryService } from 'src/app/services/inventory/inventory.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-kardex-article',
  templateUrl: './kardex-article.component.html',
  styleUrls: ['./kardex-article.component.scss'],
  standalone: false
})
export class KardexArticleComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formInventoryReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listInventory: any[] = [];
  nameWindow: string = 'Informe de Kardex por Artículo';
  projectName = ProjectName.projectName;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private inventoryService: InventoryService,
    private inventoryPeriodService: InventoryPeriodService,
    private inventoryReportService: InventoryReportService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
    this.getLastInventoryPeriod();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeArticle(articleId: number | null) {
    if (articleId === 0 || articleId === null) {
      this.formInventoryReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeInventory(inventoryId: number | null) {
    if (inventoryId === 0 || inventoryId === null) {
      this.formInventoryReportFilter.controls["inventoryId"].patchValue(0);
    }
    this.changeFilter();
  }

  clear() {
    this.changeFilter();
    this.validateForm();
    this.getLastInventoryPeriod();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formInventoryReportFilter.get("dateInitial")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);
      const dateFinal = this.formInventoryReportFilter.get("dateFinal")?.value;
      const finalDate = `${dateFinal.getFullYear()}-${(dateFinal.getMonth() + 1).toString().padStart(2, '0')}`;
      const periodFinal = finalDate.slice(5);
      const yearFinal = finalDate.slice(0, -3);

      const modelInventoryReportFilter = this.formInventoryReportFilter.value as InventoryReportFilterModel;
      modelInventoryReportFilter.year = parseInt(year);
      modelInventoryReportFilter.period = parseInt(period);
      modelInventoryReportFilter.yearFinal = parseInt(yearFinal);
      modelInventoryReportFilter.periodFinal = parseInt(periodFinal);
      try {
        const response = await firstValueFrom(this.inventoryReportService.exportInventoryReportKardexArticle(modelInventoryReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Kardex por Artículo.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists() {
    try {
      const [inventories, articles] = await Promise.all([
        firstValueFrom(this.inventoryService.getAllInventoriesForSelectAsync()),
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync()),
      ]);
      this.listInventory = inventories;
      this.listArticle = articles;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getLastInventoryPeriod() {
    try {
      const inventoryPeriod = await firstValueFrom(this.inventoryPeriodService.getLastInventoryPeriod());
      this.formInventoryReportFilter.controls["dateInitial"].patchValue(moment(inventoryPeriod.initialDate).startOf('month').toDate());
      this.formInventoryReportFilter.controls["dateFinal"].patchValue(moment(inventoryPeriod.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar el periodo de inventario:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formInventoryReportFilter.controls).forEach(key => {
      const control = this.formInventoryReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInventoryReportFilter.valid) {
      const dateInitial = this.formInventoryReportFilter.get("dateInitial")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);
      const dateFinal = this.formInventoryReportFilter.get("dateFinal")?.value;
      const finalDate = `${dateFinal.getFullYear()}-${(dateFinal.getMonth() + 1).toString().padStart(2, '0')}`;
      const periodFinal = finalDate.slice(5);
      const yearFinal = finalDate.slice(0, -3);

      if (finalDate < initialDate) {
        this.toastHandlerService.showToast('warning', 'El período final no puede ser menor al período inicial.');
        return;
      }

      const modelInventoryReportFilter = this.formInventoryReportFilter.value as InventoryReportFilterModel;
      modelInventoryReportFilter.year = parseInt(year);
      modelInventoryReportFilter.period = parseInt(period);
      modelInventoryReportFilter.yearFinal = parseInt(yearFinal);
      modelInventoryReportFilter.periodFinal = parseInt(periodFinal);

      try {
        this.dataReport = await firstValueFrom(this.inventoryReportService.inventoryReportKardexArticle(modelInventoryReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      dateInitial: {
        required: 'El campo Período Inicial es obligatorio.'
      },
      dateFinal: {
        required: 'El campo Período Final es obligatorio.'
      },
      inventoryId: {
        required: 'El campo Inventario es obligatorio.',
        min: 'Debe seleccionar un Inventario válido.'
      },
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInventoryReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formInventoryReportFilter = this.formBuilder.group({
      dateInitial: ['', [Validators.required]],
      dateFinal: ['', [Validators.required]],
      inventoryId: [0, [Validators.required, Validators.min(1)]],
      articleId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

}
